/**
 * MER-581: a chart is painted in the colour of the domain it belongs to.
 *
 * MER-578 gave every KPI tile a domain rail. Charts had no equivalent: they
 * drew from a fixed NAMED_COLORS map in which `brand`, `teal` and `blue` were
 * byte-identical, so three different declared colours painted the same teal and
 * a Revenue chart was indistinguishable from a Disposal one.
 *
 * TWO JOBS, TWO ANSWERS. Within one chart the colour carries SERIES identity;
 * across charts it carries DOMAIN. Because a chart only ever belongs to one
 * domain, two domain hues never appear in the same plot -- which is exactly why
 * the safety-vs-operations pair that fails all-pairs CVD (MER-579) can never
 * bite here.
 *
 *   1 series   -> the flat domain hue. 15 of 26 charts.
 *   n series   -> a single-hue ordinal ramp of that domain, light to dark.
 *                 Manifest declaration order is best-to-worst in every
 *                 multi-series case that matters (Current..Over 120,
 *                 <7d..>60d), so the darkest step lands on the worst bucket
 *                 for free.
 *
 * OPAQUE ON PURPOSE. The old map painted fills at 0.70-0.85 alpha over white,
 * so any hex validated against a white surface painted LIGHTER than validated
 * and every contrast/CVD number was wrong at paint time. These composite the
 * alpha against the surface up front and emit opaque hex, so what is checked is
 * what is drawn.
 *
 * Plain .mjs so node:test imports it with no build step.
 */

// Kept in lockstep with brand.css --domain-*; a guard test parses the CSS and
// fails if these drift. A chart quietly painting last month's palette is not
// something anyone would notice by looking.
const DOMAIN_HEX = {
  disposal: "#009184",
  operations: "#3f57c4",
  financial: "#b05f0f",
  safety: "#9243b5",
};

const FALLBACK = "operations";

function clamp01(x) {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

export function hexToRgb(hex) {
  const h = String(hex).replace("#", "").trim();
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }) {
  const p = (v) =>
    Math.round(clamp01(v / 255) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${p(r)}${p(g)}${p(b)}`;
}

/** Mix `hex` toward `target` by `amount` (0 = hex, 1 = target). */
export function mix(hex, target, amount) {
  const a = hexToRgb(hex);
  const b = hexToRgb(target);
  const t = clamp01(amount);
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });
}

/** The flat hue for a domain. Unknown keys fall back rather than emit null. */
export function domainHex(domain) {
  return DOMAIN_HEX[domain] ?? DOMAIN_HEX[FALLBACK];
}

export const DOMAIN_KEYS = Object.freeze(Object.keys(DOMAIN_HEX));

// The ramp only ever DARKENS. Lightening was the obvious move and it does not
// survive contact with the palette: mixing the disposal teal even 25% toward
// white drops it to 2.72:1 against the surface, under the 3:1 floor, because
// the base hue itself only holds ~3.5:1. Every lightened step would have been a
// bar you strain to see. Anchoring the ramp at the base means series 1 carries
// the full domain hue and every later step is at least as legible.
const RAMP_MAX_DARKEN = 0.55;

/**
 * n colours for one chart, all in the same domain hue.
 *
 * @param {string} domain  key from DOMAIN_KEYS
 * @param {number} count   number of series
 * @returns {Array<{bg: string, border: string}>} opaque, paint-ready
 */
export function seriesColors(domain, count) {
  const base = domainHex(domain);
  const n = Number.isFinite(count) ? Math.max(1, Math.floor(count)) : 1;
  const border = mix(base, "#000000", 0.22);

  if (n === 1) return [{ bg: base, border }];

  const out = [];
  for (let i = 0; i < n; i += 1) {
    // Base -> dark across declaration order, which is best-to-worst in every
    // multi-series case that matters, so the darkest step lands on the worst
    // bucket without anyone ordering it by hand.
    const t = i / (n - 1);
    const hex = mix(base, "#000000", RAMP_MAX_DARKEN * t);
    out.push({ bg: hex, border: mix(hex, "#000000", 0.22) });
  }
  return out;
}
